import { Fragment, ReactNode, useId, useState } from 'react';
import { useAtom } from "jotai";
import { useTranslation } from 'react-i18next';
import { authenticationAtom } from '../State';

import { Entity, LegalPerson, Product, Asset, getEntityType, Relationship, Cardinality, Query, EntityQuery } from '@the-real-insight/tri-model';

import Section from '../components/Section';

import { userAPI } from '../UserAPI';

import { configuration } from '../Configuration';

import { useAdministrationAPI } from '../AdministrationAPI';
import { ApplicationError, UnauthorizedError } from '../general/Errors';

import { useModalContext } from '../components/ModalProvider';
import { useUrlPageState } from '../utilities/dataTablePagination';

import type { Column, ColumnDef } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, IndentIncrease } from 'lucide-react';

import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/sonner';

const publicEntityTypes = [LegalPerson, Product, Asset];

/**
 * The fields this panel shows and lets someone edit.
 *
 * Internal fields are left out — they belong to the record, not to the data. The change log in
 * particular would arrive here as an array of objects, which the table renders as React children
 * and which the form would offer as an editable text input.
 */
const visibleFields = (entityType: any): any[] =>
    (entityType?.fields ?? []).filter((field: any) => !field.internal);

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
    const sorted = column.getIsSorted();

    return (
        <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === 'asc')}
        >
            {children}
            {sorted === 'asc'
                ? <ArrowUp aria-hidden className="h-3.5 w-3.5" />
                : sorted === 'desc'
                    ? <ArrowDown aria-hidden className="h-3.5 w-3.5" />
                    : <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />}
        </Button>
    );
}

/** Spaltenkopf ohne Sortierung — gleiche Schriftstärke/Farbe wie die sortierbaren Köpfe. */
const plainHeader = (text: string) => () => <span className="font-bold text-foreground">{text}</span>;

/** Leerzustand einer Tabelle: Überschrift und Erklärung. */
function TableEmptyState({ title, hint }: { title: string; hint?: string }) {
    return (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
        </div>
    );
}

interface Properties {
}

export default function DataManagementPanel(_properties: Properties) {
    const { t } = useTranslation();
    const entityListPagination = useUrlPageState('dataManagementPage');
    const { pushError } = useModalContext() as any;
    const [_authentication] = useAtom(authenticationAtom) as any;
    const { updateEntity } = useAdministrationAPI();
    const fieldId = useId();
    const [entityType, setEntityType] = useState() as any;
    const [entities, setEntities] = useState([]) as any;
    const [selectedEntity, setSelectedEntity] = useState() as any;
    const [entity, setEntity] = useState() as any;
    const [filter, _setFilter] = useState('') as any;
    const [traversalPath, setTraversalPath] = useState([]) as any;
    const [loading, setLoading] = useState(false) as any;
    const [modified, setModified] = useState(false) as any;
    const locale = 'de';

    const entityTypeId = `${fieldId}-entity-type`;
    const filterInputId = `${fieldId}-filter`;

    const entityTypeOptions = publicEntityTypes.map((type: any) => {
        return { key: type.type.name, text: type.type.getLabel(locale) }
    });

    const getEntityListColumns = (): ColumnDef<any>[] => {
        if (!entityType) {
            return [];
        }

        return visibleFields(getEntityType(entityType)).map((field: any) => {
            const label = field.getLabel(locale);
            const sortable = field.type.name !== 'Image' && field.type !== JSON;

            return {
                id: field.name,
                accessorFn: (entity: any) => field.type === Date ? new Date(entity[field.name]).getTime() : entity[field.name],
                enableSorting: sortable,
                header: sortable
                    ? ({ column }: { column: Column<any, unknown> }) => <SortableHeader column={column}>{label}</SortableHeader>
                    : plainHeader(label),
                cell: ({ row }: { row: { original: any } }) => {
                    const entity = row.original;

                    if (field.type.name === 'Image') {
                        return <img
                            className="h-16 w-24 max-w-full rounded object-cover object-center"
                            src={`${configuration.productionAPIURL}/documents/${entity[field.name]}`}
                            alt={label}
                        />;
                    } else if (field.type === JSON) {
                        return <span></span>;
                    } else if (field.type === Date) {
                        const value = entity[field.name];
                        if (!value) return <div>-</div>;
                        const date = new Date(value);
                        return <div className="whitespace-nowrap">{isNaN(date.getTime()) ? '-' : date.toLocaleDateString(locale)}</div>;
                    } else {
                        return <div className="min-w-0 break-words">{entity[field.name] ? entity[field.name] : '-'}</div>;
                    }
                },
            };
        });
    };

    const selectEntityType = async (entityType: string) => {
        if (!entityType) {
            return;
        }

        setLoading(true);
        setEntityType(entityType)

        try {
            const result: any = await userAPI.query(new Query(new EntityQuery(getEntityType(entityType).type)));

            setEntities(result);
            setEntity(null);
            setSelectedEntity(null);
        } catch (error) {
        } finally {
            setLoading(false);
        }
    }

    const selectEntity = async (entity: Entity) => {
        if (!entity) {
            return;
        }

        const result = await userAPI.query(Query.nextNeighborQuery(entity as Entity));

        setEntityType(result[0].getTypeName());
        setSelectedEntity(result[0]);

        const entityClass = result[0].getTypeClass();

        setEntity(new entityClass(result[0]));
    }

    const filterChanged = async (_value: string) => {
    }

    const onTraversalPathClicked = (entry: any) => {
        if (traversalPath.length > 1) {
            setTraversalPath(traversalPath.slice(0, traversalPath.indexOf(entry) + 1));
        }

        selectEntity(entry.entity);
    }

    const saveEntity = () => {
        try {
            updateEntity(entity);
            setModified(false);
            toast.success(t('settingsPanel.dataManagementPanel.entitySaved'));
        } catch (error: any) {
            if (error instanceof ApplicationError) {
                pushError(error);
            } else if (error instanceof UnauthorizedError) {
                pushError(error);
            } else {
                pushError(error);
            }
        }
    };

    /** Ein Feld des ausgewählten Datensatzes im Bearbeitungsformular. */
    const renderEntityField = (field: any) => {
        const label = field.getLabel(locale);
        const inputId = `${fieldId}-field-${field.name}`;

        if (field.type === Date) {
            const value = entity[field.name] ? new Date(entity[field.name]) : null;

            return <div key={field.name} className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor={inputId}>{label}</Label>
                <Input
                    id={inputId}
                    readOnly
                    className="bg-muted"
                    value={value && !isNaN(value.getTime()) ? value.toLocaleDateString(locale) : ''}
                />
            </div>;
        } else if (field.type === String) {
            return <div key={field.name} className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor={inputId}>{label}</Label>
                <Input
                    id={inputId}
                    value={entity[field.name] ?? ''}
                    onChange={(event) => {
                        const newEntity = { ...entity };

                        newEntity[field.name] = event.target.value;

                        setEntity(new (entity.getTypeClass())(newEntity));
                        setModified(true);
                    }}
                />
            </div>;
        } else if (field.type === JSON) {
            return <div key={field.name} className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor={inputId}>{label}</Label>
                <Textarea
                    id={inputId}
                    disabled
                    className="font-mono"
                    value={JSON.stringify(entity[field.name])}
                    readOnly
                />
            </div>;
        } else if (field.type.name === 'Image') {
            return <div key={field.name} className="flex min-w-0 flex-col gap-1.5">
                <p className="text-sm font-medium">{label}</p>
                <img
                    className="h-24 w-36 max-w-full rounded object-cover object-center"
                    src={`${configuration.productionAPIURL}/documents/${entity[field.name]}`}
                    alt={label}
                />
            </div>;
        } else if (field.type.name === 'Document') {
            return <div key={field.name} className="flex min-w-0 flex-col gap-1.5">
                <p className="text-sm font-medium">{label}</p>
                <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="ghost" className="gap-2">
                        <IndentIncrease aria-hidden className="h-4 w-4" />
                        {t('global.select')}
                    </Button>
                </div>
            </div>;
        }

        return <p key={field.name} className="text-sm font-medium">{label}</p>;
    };

    /** Ein Feld des über eine Beziehung erreichten Datensatzes (nur Anzeige). */
    const renderRelationshipField = (relationship: Relationship, field: any) => {
        const label = field.getLabel(locale);
        const target = (selectedEntity as any)[(relationship as any).name];
        let value: ReactNode = field.type.name;

        if (field.type === Date) {
            const raw = target[field.name];
            const date = raw ? new Date(raw) : null;
            value = !date || isNaN(date.getTime()) ? '-' : date.toLocaleDateString(locale);
        } else if (field.type === String) {
            value = target[field.name] ? target[field.name] : '-';
        } else if (field.type === JSON || field.type.name === 'Image') {
            value = '';
        } else if (field.type.name === 'Document') {
            value = <Button type="button" variant="ghost" className="gap-2">
                <IndentIncrease aria-hidden className="h-4 w-4" />
                {t('global.select')}
            </Button>;
        }

        return <Fragment key={field.name}>
            <dt className="font-bold">{label}</dt>
            <dd className="min-w-0 break-words">{value}</dd>
        </Fragment>;
    };

    return <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
        <div className="flex min-w-0 max-w-sm flex-col gap-1.5">
            <Label htmlFor={entityTypeId}>{t('settingsPanel.dataManagementPanel.entityType')}</Label>
            <Select value={entityType ?? ''} onValueChange={(value) => selectEntityType(value)}>
                <SelectTrigger id={entityTypeId}>
                    <SelectValue placeholder={`${t('global.pleaseSelect')} ...`} />
                </SelectTrigger>
                <SelectContent>
                    {entityTypeOptions.map((option: any) => (
                        <SelectItem key={option.key} value={option.key}>{option.text}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
        {entityType
            ?
            <>
                <Section title={getEntityType(entityType).type.getLabel(locale)}>
                    <div className="flex min-w-0 flex-col gap-4">
                        <div className="flex min-w-0 max-w-xs flex-col gap-1.5">
                            <Label htmlFor={filterInputId}>{t('settingsPanel.dataManagementPanel.filter')}</Label>
                            <Input
                                id={filterInputId}
                                value={filter}
                                onChange={(event) => filterChanged(event.target.value)}
                            />
                        </div>
                        <div className="min-w-0 overflow-x-auto">
                            <DataTable
                                data={entities}
                                columns={getEntityListColumns()}
                                pageSize={5}
                                loading={loading}
                                page={entityListPagination.page}
                                onPageChange={entityListPagination.onPageChange}
                                previousAriaLabel={t('global.back')}
                                nextAriaLabel={t('global.next')}
                                onRowClick={(row: any) => selectEntity(row)}
                                rowClassName={(row: any) =>
                                    selectedEntity && selectedEntity._id === row._id ? 'bg-accent hover:bg-accent' : undefined}
                                emptyMessage={<TableEmptyState
                                    title={t('settingsPanel.dataManagementPanel.noEntitiesTitle')}
                                    hint={t('settingsPanel.dataManagementPanel.noEntitiesHint')}
                                />}
                            />
                        </div>
                    </div>
                </Section>
                {traversalPath.length > 0
                    ?
                    <Breadcrumb aria-label={t('settingsPanel.dataManagementPanel.traversalPath')}>
                        <BreadcrumbList>
                            {traversalPath.map((entry: any, index: number) => {
                                const label = `${entry.relationship ? entry.relationship.getLabel(locale) : t('settingsPanel.dataManagementPanel.selection')}: ${entry.entity.constructor.type.getLabel(locale)}`;

                                return <Fragment key={entry.entity._id}>
                                    {index > 0 ? <BreadcrumbSeparator /> : null}
                                    <BreadcrumbItem>
                                        {index === traversalPath.length - 1
                                            ?
                                            <BreadcrumbPage>{label}</BreadcrumbPage>
                                            :
                                            <Button
                                                variant="link"
                                                className="h-auto px-0 py-0 text-sm font-normal text-muted-foreground hover:text-foreground"
                                                onClick={() => onTraversalPathClicked(entry)}
                                            >
                                                {label}
                                            </Button>}
                                    </BreadcrumbItem>
                                </Fragment>;
                            })}
                        </BreadcrumbList>
                    </Breadcrumb>
                    :
                    null}
                <div className="flex min-w-0 flex-wrap gap-4">
                    <div className="min-w-0 max-w-2xl flex-1 basis-[320px]">
                        <Section title={getEntityType(entityType).type.getLabel(locale)}>
                            <div className="flex min-w-0 flex-col gap-4">
                                <div className="flex flex-wrap justify-end gap-2">
                                    <Button onClick={saveEntity} disabled={!modified}>
                                        {t('global.save')}
                                    </Button>
                                </div>
                                {entity
                                    ?
                                    <div className="flex min-w-0 flex-col gap-3">
                                        {visibleFields(getEntityType(entityType)).map(renderEntityField)}
                                    </div>
                                    :
                                    <div className="flex flex-col items-center gap-2 py-4 text-center">
                                        <div className="text-sm font-semibold text-foreground">
                                            {t('settingsPanel.dataManagementPanel.noSelection')}
                                        </div>
                                        <div className="max-w-prose text-sm text-muted-foreground">
                                            {t('settingsPanel.dataManagementPanel.noSelectionHint')}
                                        </div>
                                    </div>}
                            </div>
                        </Section>
                    </div>
                    <div className="flex min-w-0 flex-1 basis-[320px] flex-col gap-4">
                        {getEntityType(entityType).relationships.map((relationship: Relationship) => {
                            const name = (relationship as any).name;
                            const cardinality = (relationship as any).cardinality;
                            const hasTarget = selectedEntity && (
                                ((cardinality === Cardinality.OneToOne || cardinality === Cardinality.ManyToOne) && selectedEntity[name]) ||
                                ((cardinality === Cardinality.OneToMany || cardinality === Cardinality.ManyToMany) && selectedEntity[name].length > 0));

                            return <Section key={name} title={relationship.getLabel(locale)}>
                                {hasTarget
                                    ?
                                    <div className="flex min-w-0 flex-col gap-3">
                                        <div className="flex flex-wrap justify-end gap-2">
                                            <Button
                                                variant="ghost"
                                                className="gap-2"
                                                onClick={() => {
                                                    setTraversalPath([...traversalPath, {
                                                        relationship,
                                                        entity: selectedEntity[name],
                                                    }]);
                                                    selectEntity(selectedEntity[name]);
                                                }}>
                                                <IndentIncrease aria-hidden className="h-4 w-4" />
                                                {t('global.select')}
                                            </Button>
                                        </div>
                                        <dl className="grid min-w-0 grid-cols-[minmax(8rem,max-content)_1fr] gap-x-4 gap-y-2 text-sm">
                                            {visibleFields((relationship as any).type).map((field: any) => renderRelationshipField(relationship, field))}
                                        </dl>
                                    </div>
                                    :
                                    <span className="text-sm text-muted-foreground">{t('settingsPanel.dataManagementPanel.noRelationshipSet')}</span>
                                }
                            </Section>;
                        })}
                    </div>
                </div>
            </>
            :
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed p-6 text-center">
                <div className="text-sm font-semibold text-foreground">
                    {t('settingsPanel.dataManagementPanel.chooseEntityTypeTitle')}
                </div>
                <div className="max-w-prose text-sm text-muted-foreground">
                    {t('settingsPanel.dataManagementPanel.chooseEntityTypeHint')}
                </div>
            </div>
        }
    </div>;
}
